import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import DrawingsPanel from "@/features/drawings/panel/DrawingsPanel";
import type { DrawingsPanelOps, PanelItem } from "@/features/drawings/panel/types";
import {
  createItemForQuotationDrawing,
  createItemsForQuotationDrawings,
  deleteQuotationDrawing,
  deleteQuotationDrawings,
  importQuotationDrawings,
  linkQuotationDrawing,
  matchQuotationDrawings,
  rebuildQuotationDrawingThumbnails,
  rereadQuotationDrawingNumbers,
  updateQuotationDrawing,
  type QuotationItem,
} from "./api";
import { quotationDrawingsQuery } from "./drawingsQuery";

/**
 * 見積詳細の「図面」タブ。
 *
 * 中身は案件と共通（[[DrawingsPanel]]）。ここは**見積の経路と文言を与えるだけ**の
 * 包み。API が返す名前（`quotation_item_id`、`project_number`）を、パネルが扱う
 * 中立な形（`item_id`、`origin_note`）へ写す役も持つ。
 */
export default function QuotationDrawingsPanel({
  quotationId,
  canWrite,
  items,
}: {
  quotationId: number;
  canWrite: boolean;
  /** 紐付け先の候補にする明細（製作対象の行だけ渡す）。 */
  items: QuotationItem[];
}) {
  const navigate = useNavigate();

  const ops = useMemo<DrawingsPanelOps>(
    () => ({
      queryKey: quotationDrawingsQuery(quotationId).queryKey,
      // 紐付けが変わると明細側の図面リンク・版数の表示も変わる。
      invalidateKeys: [["quotation-items", quotationId]],
      eventsPath: `/quotations/${quotationId}/drawings/events`,

      list: () => quotationDrawingsQuery(quotationId).queryFn(),
      importFile: (file) => importQuotationDrawings(quotationId, file),
      saveNumber: (drawingId, drawingNumber) =>
        updateQuotationDrawing(quotationId, drawingId, {
          drawing_number: drawingNumber,
        }),
      link: (drawingId, itemId) => linkQuotationDrawing(quotationId, drawingId, itemId),
      createItem: (drawingId) => createItemForQuotationDrawing(quotationId, drawingId),
      createItems: (drawingIds) =>
        createItemsForQuotationDrawings(quotationId, drawingIds),
      match: async (drawingIds, dryRun) => {
        const res = await matchQuotationDrawings(quotationId, drawingIds, dryRun);
        return { results: res.results };
      },
      remove: (drawingId) => deleteQuotationDrawing(quotationId, drawingId),
      removeMany: (drawingIds) => deleteQuotationDrawings(quotationId, drawingIds),
      rebuildThumbnails: () => rebuildQuotationDrawingThumbnails(quotationId),
      rereadNumbers: (drawingIds) =>
        rereadQuotationDrawingNumbers(quotationId, drawingIds),

      // 紐付けたあとにやることは金額を入れること。この画面で一番押される導線。
      openEditor: (drawing) =>
        navigate(`/quotations/${quotationId}/items/${drawing.item_id}`),
      editorLabel: "見積を入力",

      matchDescription: "見積明細の図番と突き合わせます。",
      linkHint: "製作対象の明細のうち、まだ図面が付いていない行だけが候補に出ます。",
      createItemHint:
        "依頼に載っていない図面のための操作です。品名は図番で代用し、数量は 1 で起こします。",
    }),
    [quotationId, navigate],
  );

  const panelItems = useMemo<PanelItem[]>(
    () =>
      items.map((item) => ({
        item_id: item.quotation_item_id,
        sequence: item.sequence,
        item_name: item.item_name,
        drawing_number: item.drawing_number,
      })),
    [items],
  );

  return <DrawingsPanel ops={ops} canWrite={canWrite} items={panelItems} />;
}
